import React, { useState, useEffect, useRef } from "react"
import PropTypes from "prop-types"
import { Slider, Input, Button, Flex } from "antd"
import { useTranslation } from "react-i18next"
import styles from "./index.module.css"
import { Text } from "ui/Typography"
import { useSelector } from "react-redux"

export default function PriceFilterDropdown({ onChange }) {
  const { t } = useTranslation()
  const [priceRange, setPriceRange] = useState([])
  const filterOptions = useSelector((state) => state.trips?.filter_options?.lists)
  const onChangeRef = useRef(onChange)
  const debounceRef = useRef(null)
  const absoluteRange = filterOptions?.price_range || [0, 0]

  useEffect(() => {
    setPriceRange(filterOptions?.price_range)
  }, [filterOptions])

  useEffect(() => {
    onChangeRef.current = onChange
  }, [onChange])

  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current)
    }
  }, [])

  const emitDebouncedChange = (newRange) => {
    if (debounceRef.current) clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(() => {
      onChangeRef.current?.(newRange)
    }, 500)
  }

  const handleSliderChange = (newRange) => {
    setPriceRange(newRange)
    emitDebouncedChange(newRange)
  }

  const handleInputChange = (idx, val) => {
    let nextVal = Number(val)
    if (Number.isNaN(nextVal)) return
    // Clamp to absolute bounds from API
    if (idx === 0) nextVal = Math.max(absoluteRange[0], nextVal)
    if (idx === 1) nextVal = Math.min(absoluteRange[1], nextVal)
    const base = priceRange?.length === 2 ? priceRange : absoluteRange
    const newRange = [...base]
    newRange[idx] = nextVal
    // Keep min <= max
    if (idx === 0 && newRange[1] != null && nextVal > newRange[1]) newRange[1] = nextVal
    if (idx === 1 && newRange[0] != null && nextVal < newRange[0]) newRange[0] = nextVal
    setPriceRange(newRange)
    emitDebouncedChange(newRange)
  }

  const handleClear = () => {
    setPriceRange(absoluteRange)
    emitDebouncedChange(absoluteRange)
  }

  return (
    <div className={styles.dropdownContent}>
      <Slider
        range
        min={absoluteRange[0]}
        max={absoluteRange[1]}
        value={priceRange?.length === 2 ? priceRange : absoluteRange}
        onChange={handleSliderChange}
        className={styles.priceSlider}
      />
      <Flex gap={8} className="mb-s">
        <div>
          <Text strong>{t("global.min")}</Text>
          <Input
            prefix="$"
            value={priceRange?.length === 2 ? priceRange[0] : absoluteRange[0]}
            min={absoluteRange[0]}
            max={priceRange?.length === 2 ? priceRange[1] : absoluteRange[1]}
            type="number"
            onChange={(e) => handleInputChange(0, e.target.value)}
          />
        </div>
        <span className={styles.toSeparator}>{t("global.to")}</span>
        <div>
          <Text strong>{t("global.max")}</Text>
          <Input
            prefix="$"
            value={priceRange?.length === 2 ? priceRange[1] : absoluteRange[1]}
            min={priceRange?.length === 2 ? priceRange[0] : absoluteRange[0]}
            max={absoluteRange[1]}
            type="number"
            onChange={(e) => handleInputChange(1, e.target.value)}
          />
        </div>
      </Flex>
      <Button type="link" onClick={handleClear}>
        {t("filterTags.clear")}
      </Button>
    </div>
  )
}

PriceFilterDropdown.propTypes = {
  onChange: PropTypes.func
}
